import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '@yoast/ui-library';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getGetBrandsQueryKey,
  useGetBrands,
  useUpdateBrandSettings,
} from '../api/generated/insights';

interface ModalAlertBrandDisabledProps {
  className?: string;
}

const ModalAlertBrandDisabled: React.FC<ModalAlertBrandDisabledProps> = ({
  className,
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showBrandDisabledModal, setShowBrandDisabledModal] = useState(false);

  const { data: brands } = useGetBrands({
    query: {
      select: ({ data }) => data?.brands,
    },
  });

  const {
    mutateAsync: mutateBrandSettingsAsync,
    isPending: isPendingBrandSettingsUpdate,
  } = useUpdateBrandSettings({
    mutation: {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [getGetBrandsQueryKey()],
        });
      },
    },
  });

  // Filter brands with disabled notification
  const brandsWithDisabledNotification = useMemo(() =>
    brands?.filter(
      (brand) =>
        brand.settings?.showAutomaticAnalysisDisabledNotification === true,
    ) ?? [], [brands]);

  // ALERT MODAL: show if any brands have been disabled automatically
  useEffect(() => {
    setShowBrandDisabledModal(brandsWithDisabledNotification.length !== 0);
  }, [brandsWithDisabledNotification]);


  // Modal close handler
  const onClose = async () => {
    // Use the updateBrandSettings mutation hook to update brand settings
    const promises = brandsWithDisabledNotification.map((brand) =>
        mutateBrandSettingsAsync({
          brandId: String(brand.id),
          data: { showAutomaticAnalysisDisabledNotification: false },
        }),
    );
    await Promise.all(promises);
    setShowBrandDisabledModal(false);

  };
  // Confirm handler: disables notification for all affected brands
  const handleConfirmDisabledNotification = async () => {
    await onClose();
    navigate('/settings');

  };

  // Modal content
  const title =
    brandsWithDisabledNotification.length === 1
      ? 'One brand has been deactivated'
      : 'Multiple brands have been deactivated';

  const description =
    brandsWithDisabledNotification.length === 1 ? (
      <>
        <p>
          You recently lost a subscription. Because of this, we could not
          analyze all your active brands.
        </p>
        <p>
          The brand <b>{brandsWithDisabledNotification?.[0]?.name}</b> was
          deactivated, since it was the most recently analyzed.
        </p>
        <p>
          If you prefer, you can go to <b>Settings</b> and deactivate a
          different brand instead.
        </p>
      </>
    ) : (
      <>
        <p>
          You recently lost a subscription. Because of this, we could not
          analyze all your active brands.
        </p>
        <p>
          The following brands were deactivated, since they were the most
          recently analyzed:
        </p>
        <ul>
          {brandsWithDisabledNotification.map((brand) => (
            <li key={brand.id}>
              <b>{brand.name}</b>
            </li>
          ))}
        </ul>
        <p>
          If you prefer, you can go to <b>Settings</b> and deactivate different
          brand(s) instead.
        </p>
      </>
    );

  if (!showBrandDisabledModal) return null;

  return (
    <Modal
      isOpen={showBrandDisabledModal}
      onClose={handleConfirmDisabledNotification}
      position="center"
      as="div"
      className={className}
    >
      <Modal.Panel>
        <div className="yst-flex yst-items-start yst-justify-stretch yst-gap-4">
          <div className="yst-flex-1">
            <Modal.Container.Header className="yst-mb-3 yst-w-full">
              <Modal.Title as="h3" className="yst-text-lg yst-font-medium">
                {title}
              </Modal.Title>
            </Modal.Container.Header>
            <Modal.Container.Content className="yst-mb-4 yst-space-y-3">
              {description}
            </Modal.Container.Content>
          </div>
        </div>
        <Modal.Container.Footer className="yst-mt-3 yst-flex yst-justify-end yst-gap-2">
          <Button
            variant="secondary"
            onClick={handleConfirmDisabledNotification}
          >
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmDisabledNotification}
            isLoading={isPendingBrandSettingsUpdate}
            disabled={isPendingBrandSettingsUpdate}
          >
            Go to settings
          </Button>
        </Modal.Container.Footer>
      </Modal.Panel>
    </Modal>
  );
};

export default ModalAlertBrandDisabled;
